/**
 * Tema claro/escuro — constantes compartilhadas.
 * Tokens de cor vivem em `assets/theme/_tokens.scss`; guia em docs/tema/GUIA-TOKENS.md.
 */

export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

/** Chave do localStorage — lida também por `public/theme-init.js` antes do React subir. */
export const THEME_STORAGE_KEY = 'ee-theme';

export const DEFAULT_THEME_PREFERENCE: ThemePreference = 'light';

/**
 * Prefixos de rota cujas telas já foram migradas para os tokens e funcionam no escuro.
 * Fora destes prefixos a página fica no claro, mesmo com o escuro escolhido — nada de
 * tela "meio escura". Ao concluir um menu: adicione o prefixo AQUI e no array de
 * `public/theme-init.js` (o teste `constants/theme.test.ts` acusa se divergirem).
 * Ordem livre; `/` sozinho casa só a raiz (home); termine com `$` para casar só a rota exata
 * (ex.: `'/flux$'` = hub do Flux, sem levar junto `/flux/videos`, que é de outro menu).
 */
export const DARK_READY_ROUTES: string[] = [
  '/',
  '/home',
  '/fileviewer',
  '/relatorios',
  '/flux$',
  '/flux/upload',
  '/flux/personalized',
  '/flux/automatic',
  '/flux/about',
  '/windx',
  '/meteorology$',
  '/meteorology/fields',
  '/meteorology/weather',
  '/meteorology/videos',
  '/meteorology/tutorial',
  '/flux/videos',
  '/agro',
  '/admin',
  '/signin',
  '/password-recovery',
  '/password-recovery-success',
  '/password-reset',
  '/error',
  '/no-access',
  '/forbidden',
];

export function isDarkReadyPath(pathname: string): boolean {
  return DARK_READY_ROUTES.some((prefix) => {
    if (prefix === '/') return pathname === '/';
    if (prefix.endsWith('$')) return pathname === prefix.slice(0, -1) || pathname === `${prefix.slice(0, -1)}/`;
    return pathname === prefix || pathname.startsWith(`${prefix}/`);
  });
}

/** Cor da barra do navegador mobile (`<meta name="theme-color">`) em cada tema. */
export const THEME_META_COLOR: Record<ResolvedTheme, string> = {
  light: '#FFFFFF',
  dark: '#0E1420',
};

/**
 * Tokens semânticos (sem o prefixo `--ee-`). Use com `cssVar()` em estilo inline ou em
 * prop de cor — ex.: `<Icon color={cssVar('text-muted')} />` não funciona em atributo
 * SVG; prefira `color="currentColor"` + CSS. `constants/theme.test.ts` garante que todo
 * nome daqui existe nos dois temas do SCSS.
 */
export type ThemeToken =
  | 'bg-canvas' | 'bg-canvas-subtle' | 'bg-surface' | 'bg-surface-raised' | 'bg-subtle'
  | 'bg-muted' | 'bg-hover' | 'bg-selected' | 'bg-disabled' | 'bg-tooltip' | 'bg-overlay'
  | 'bg-figure'
  | 'text' | 'text-strong' | 'text-secondary' | 'text-muted' | 'text-disabled'
  | 'text-on-accent' | 'text-inverse' | 'text-link' | 'text-link-hover' | 'text-accent'
  | 'text-brand' | 'text-tooltip'
  | 'border-subtle' | 'border' | 'border-strong' | 'focus-ring'
  | 'accent' | 'accent-hover' | 'accent-active' | 'accent-subtle' | 'accent-disabled'
  | 'accent-strong' | 'selection'
  | 'nav-bg' | 'nav-text' | 'nav-text-muted' | 'nav-item-hover-bg' | 'nav-item-active-bg'
  | 'nav-item-active-text' | 'nav-mobile-bg' | 'header-bg'
  | 'tab-bg' | 'tab-text' | 'tab-hover-bg' | 'tab-active-text'
  | 'success-fg' | 'success-bg' | 'success-border' | 'success-solid' | 'success-strong'
  | 'success-strong-hover'
  | 'warning-fg' | 'warning-bg' | 'warning-border' | 'warning-solid'
  | 'danger-fg' | 'danger-bg' | 'danger-border' | 'danger-solid'
  | 'info-fg' | 'info-bg' | 'info-border' | 'info-solid' | 'neutral-solid'
  | 'status-attention-bg' | 'status-attention-fg' | 'status-attention-solid'
  | 'status-progress-bg' | 'status-progress-fg' | 'status-progress-solid'
  | 'status-done-bg' | 'status-done-fg' | 'status-done-solid'
  | 'status-cancelled-bg' | 'status-cancelled-fg' | 'status-cancelled-solid'
  | 'chart-text' | 'chart-axis' | 'chart-grid'
  | 'chart-tooltip-bg' | 'chart-tooltip-border' | 'chart-tooltip-text'
  | 'shadow-sm' | 'shadow-md' | 'shadow-lg';

export const cssVar = (token: ThemeToken): string => `var(--ee-${token})`;

/**
 * Chrome dos gráficos ECharts. O canvas não lê variável CSS, então os valores ficam
 * espelhados aqui — `constants/theme.test.ts` compara com `--ee-chart-*` do SCSS.
 */
export interface ChartThemeTokens {
  text: string;
  axis: string;
  grid: string;
  tooltipBg: string;
  tooltipBorder: string;
  tooltipText: string;
}

export const CHART_THEME_TOKENS: Record<ResolvedTheme, ChartThemeTokens> = {
  light: {
    text: '#4A5870',
    axis: '#8A94A3',
    grid: '#E6EAF0',
    tooltipBg: '#FFFFFF',
    tooltipBorder: '#DCE2EA',
    tooltipText: '#1F2A3D',
  },
  dark: {
    text: '#AAB4C5',
    axis: '#4A5875',
    grid: '#232D3F',
    tooltipBg: '#1C2537',
    tooltipBorder: '#2C374B',
    tooltipText: '#E6EAF1',
  },
};

/**
 * Cor de SÉRIE no escuro. Série é dado e mantém o significado, mas as cores da marca e os
 * neutros escuros somem no fundo noite. A rampa azul é invertida para manter a ordem de
 * ênfase: a 1ª série (navy, a mais forte no claro) vira a mais clara no escuro.
 * Cor fora desta tabela (ex.: escala de ONI, cor vinda do backend) passa igual.
 */
export const CHART_SERIES_DARK: Record<string, string> = {
  '#17324E': '#C9DCF3',
  '#212F58': '#C9DCF3',
  '#26548A': '#7DB4EA',
  '#598CBB': '#4D86C2',
  '#98D6F4': '#3C6EA5',
  '#000000': '#E6EAF1',
  '#262626': '#E6EAF1',
  '#434343': '#C7CEDA',
  '#585858': '#A3ADBC',
  '#999999': '#6B778C',
  '#C1C1C1': '#5E6A80',
  '#C4C4C4': '#5E6A80',
  '#D17617': '#E5893A',
  '#C22B1F': '#F0675C',
  '#D32F2F': '#F0675C',
  '#006400': '#5FBF6A',
};

/** Nome com que cada tema é registrado no ECharts (`echarts.registerTheme`). */
export const CHART_THEME_NAME: Record<ResolvedTheme, string> = {
  light: 'ee-light',
  dark: 'ee-dark',
};
